'use client';

import { ElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { ElementProps } from '@/components/sections/builder/types';
import { cn, extractYouTubeVideoId, isEmpty } from '@/lib/utils';
import React from 'react';

import { WrapperChild } from '@/components/sections/builder/wrapper';
import { Card, CardContent } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import FixedAddToCart from '@/components/sections/builder/elements/body/product-detail/fixed-add-to-cart';
import DynamicImage from '@/components/commons/dynamic-image';
import useMediaQuery from '@/hooks/useMediaQuery';
import { useCartProduct } from '@/stores/cart';
import { Badge } from '@/components/ui/badge';
import ModalImagePreview from '@/components/sections/builder/elements/body/product-detail/components/modal-image-preview';
import { StoreProduct } from '@/types/http';
import { useProductStore } from '@/stores/product';
import { ProductAssets, ProductItem } from '@/types/product';
import { useProductDetail } from '@/hooks/api/storefront/product-detail';
import { useParams } from 'next/navigation';
import Cookies from 'js-cookie';
import { Play } from 'lucide-react';
import Image from 'next/image';

export default function ElementContent({ element, preview, theme }: ElementProps) {
  const { slug: productSlug } = useParams<{ slug: string }>();
  const customerToken = Cookies.get('accessToken') as string;
  const isMobile = useMediaQuery(768);

  const [mainImage, setMainImage] = React.useState<string>('');
  const [openPreview, setOpenPreview] = React.useState<boolean>(false);
  const [mainIframe, setMainIframe] = React.useState<string | null>(null);

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );
  const cartProduct = useCartProduct(
    (state) => ({
      clear: state.clear,
      sell_price: state.sell_price,
      discount_price: state.discount_price,
    }),
    shallow
  );

  const productStore = useProductStore(
    (state) => ({
      imageVariant: state.imageVariant,
      price: state.price,
      discountPrice: state.discountPrice,
      isApplicablePricebook: state.isApplicablePricebook,
    }),
    shallow
  );

  const themeContent = preview ? builder.theme : theme;
  const content = element.data as ElementDataType;

  const childs = element.childs;

  const { data: productData, isLoading } = useProductDetail(productSlug, customerToken as string, {
    enabled: !!productSlug,
  });

  const product = productData || ({} as StoreProduct);
  const quantity = product?.available_qty || 0;

  const getUniqueImages = (assets: ProductAssets[] = []) => {
    const uniqueUrls = new Set();
    return assets.filter(
      (asset) =>
        asset.assets_type === 'image' &&
        !uniqueUrls.has(asset.assets_url) &&
        uniqueUrls.add(asset.assets_url)
    );
  };

  const images = React.useMemo(() => getUniqueImages(product.product_assets), [product]);
  const imagesVariants = React.useMemo(
    () => getUniqueImages(productStore.imageVariant),
    [productStore.imageVariant]
  );
  const videoId = product.youtube_link ? extractYouTubeVideoId(product.youtube_link) : null;

  const discountPercentage = React.useMemo(() => {
    const getDiscount = (sellPrice?: number, discountPrice?: number) => {
      return sellPrice && discountPrice ? ((sellPrice - discountPrice) / sellPrice) * 100 : 0;
    };

    if ((product?.product_items?.length || 0) > 1) {
      const discount = getDiscount(cartProduct?.sell_price, cartProduct?.discount_price);
      return discount > 0
        ? discount
        : getDiscount(
            product?.product_items?.[0]?.sell_price,
            product?.product_items?.[0]?.discount_price
          );
    }

    const firstItem = product?.product_items?.[0];
    return getDiscount(firstItem?.sell_price, firstItem?.discount_price);
  }, [product, cartProduct]);

  const BADGES_PRODUCT = [
    product?.best_seller && {
      text: themeContent?.badge?.bestSellerText || 'Best Seller',
      className:
        themeContent?.badge?.position === 'inside'
          ? 'theme_badge_best_seller_gradient'
          : 'theme_badge_best_seller',
    },
    product?.new_arrival && {
      text: themeContent?.badge?.newArrivalText || 'New Arrival',
      className:
        themeContent?.badge?.position === 'inside'
          ? 'theme_badge_new_arrival_gradient'
          : 'theme_badge_new_arrival',
    },
    discountPercentage > 0 &&
      productStore.isApplicablePricebook === false && {
        text: `${themeContent?.badge?.promotionText || 'Discount'} ${discountPercentage.toFixed(
          0
        )}%`,
        className:
          themeContent?.badge?.position === 'inside'
            ? 'theme_badge_promotion_gradient'
            : 'theme_badge_promotion',
      },
  ].filter(Boolean) as { text: string; className: string }[];

  const isSoldOut = React.useMemo(() => {
    if (product.available_qty === 0 || product.qty === 0) {
      return true;
    }

    return false;
  }, [product]);

  React.useEffect(() => {
    if (imagesVariants?.length > 0 && quantity > 0) {
      setMainImage(imagesVariants[0]?.assets_url);
    } else {
      setMainImage(images?.[0]?.assets_url);
    }
  }, [imagesVariants, quantity, images]);

  React.useEffect(() => {
    cartProduct.clear();
  }, []);

  React.useEffect(() => {
    if (preview || !product?.product_group_id) return;

    SendEventAnalytic('view_item', {
      items: [
        {
          item_id: product.product_group_id,
          item_name: product.product_name,
          item_category: product.category_name,
          item_variant: product.variants,
          price: product.sell_price,
          quantity: product.available_qty,
        },
      ],
    });

    SendEventMetapixel('ViewContent', {
      content_ids: [product.product_group_id],
      content_name: product.product_name,
      content_type: 'product',
      contents: product.product_items
        ?.filter((item): item is ProductItem & { item_id: string; stock: number } => !!item.item_id)
        .map((item) => ({
          id: item.item_id,
          quantity: item.stock,
        })),
      value: product.sell_price,
      currency: 'IDR',
    });
  }, [preview, product?.product_group_id]);

  const mediaItems = React.useMemo(() => {
    const media = [...images];

    if (product.youtube_link) {
      const videoItem = {
        assets_type: 'video',
        assets_url: product.youtube_link,
      };

      media.splice(1, 0, videoItem);
    }

    return media;
  }, [images, product.youtube_link]);

  const handleMediaClick = (url: string, type: 'image' | 'iframe') => {
    if (type === 'image') {
      setMainImage(url);
      setMainIframe(null);
    } else if (type === 'iframe') {
      setMainIframe(`${url}?autoplay=1`);
      setMainImage('');
    }
  };

  return (
    <>
      <section
        className={cn('bg-white', !content.isShowSecondaryBackground && 'bg_product_detail')}
      >
        <div className='theme_layout_page_width product_detail_wrapper mx-auto duration-100'>
          <div className='container mx-auto max-w-screen-xl px-4 lg:px-16'>
            <div
              className={cn(
                'flex flex-col ',
                (content.galleryPosition || 'horizontal') !== 'vertical' &&
                  'gap-4 md:gap-16 lg:gap-16',
                content.layoutPosition === 'right' ? 'md:flex-row-reverse' : 'md:flex-row'
              )}
            >
              <div className='w-full space-y-3 md:w-1/2'>
                <div
                  className={cn(content.enableStickyProductDetail && 'top-sticky-image md:sticky')}
                >
                  <Card
                    className='overflow-hidden border-none shadow-none'
                    style={{ borderRadius: `${builder.theme.media?.borderCornerRadius || 0}px` }}
                  >
                    <CardContent className='flex items-center justify-center p-0'>
                      {mainImage ? (
                        <DynamicImage
                          onClick={() => setOpenPreview(true)}
                          src={mainImage}
                          alt={product.product_name || 'Product image'}
                          width={600}
                          height={600}
                          sizes='(max-width: 768px) 100vw, 600px'
                          className='aspect-square w-full object-contain'
                        />
                      ) : mainIframe ? (
                        <iframe
                          width='600'
                          height='600'
                          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                          allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
                          className='aspect-square w-full object-contain'
                        ></iframe>
                      ) : null}
                    </CardContent>
                  </Card>
                  {/* carousel horizontal */}
                  {(isMobile || (content.galleryPosition || 'horizontal') === 'horizontal') && (
                    <Carousel
                      opts={{
                        align: 'start',
                        loop: true,
                      }}
                      className='mt-4 w-full'
                    >
                      <CarouselContent>
                        {isLoading
                          ? Array.from({ length: 4 }).map((_, index) => (
                              <CarouselItem key={index} className='basis-1/4'>
                                <div className='aspect-square h-auto w-full animate-pulse cursor-pointer rounded border-2 bg-gray-200 object-cover' />
                              </CarouselItem>
                            ))
                          : mediaItems?.map((item, index) => (
                              <CarouselItem key={index} className='basis-1/4'>
                                <div className='p-1'>
                                  {item.assets_type === 'image' ? (
                                    <DynamicImage
                                      src={item.assets_url}
                                      alt={`Media ${index + 1}`}
                                      width={100}
                                      height={100}
                                      className='aspect-square h-auto w-full cursor-pointer rounded border-2 object-cover'
                                      onClick={() => handleMediaClick(item.assets_url, 'image')}
                                      style={
                                        !isEmpty(content.colorSchemeProductDetail)
                                          ? {
                                              borderColor:
                                                item.assets_url === mainImage
                                                  ? content.colorSchemeProductDetail?.textSecondary
                                                  : 'transparent',
                                            }
                                          : {}
                                      }
                                    />
                                  ) : (
                                    <div
                                      className='relative aspect-square w-full cursor-pointer rounded border-2 bg-white'
                                      onClick={() => handleMediaClick(item.assets_url, 'iframe')}
                                    >
                                      <Image
                                        src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                                        alt={`Thumbnail video ${index + 1}`}
                                        width={320}
                                        height={180}
                                        className='h-full w-full rounded bg-white object-contain'
                                      />

                                      <div className='absolute inset-0 flex items-center justify-center'>
                                        <div className='rounded-full bg-slate-200 p-1'>
                                          <Play className='h-6 w-6 text-slate-100' fill='true' />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </CarouselItem>
                            ))}
                      </CarouselContent>
                      {mediaItems.length > 4 && !isMobile && (
                        <>
                          <CarouselPrevious />
                          <CarouselNext />
                        </>
                      )}
                    </Carousel>
                  )}
                </div>
              </div>
              {/* carousel  vertical*/}
              {content.galleryPosition === 'vertical' && (
                <div className='hidden items-center overflow-hidden  md:flex md:w-[100px] md:flex-col lg:w-[121px]'>
                  <Carousel
                    opts={{
                      align: 'start',
                      loop: true,
                    }}
                    className='w-3/4 items-center justify-center '
                    orientation='vertical'
                  >
                    <CarouselContent className='mt-1  md:h-[20rem] lg:h-[27rem]'>
                      {isLoading
                        ? Array.from({ length: 4 }).map((_, index) => (
                            <CarouselItem key={index} className='basis-1/4'>
                              <div className='aspect-square h-auto w-full animate-pulse cursor-pointer rounded border-2 object-cover' />
                            </CarouselItem>
                          ))
                        : mediaItems?.map((item, index) => (
                            <CarouselItem key={index} className='basis-1/4'>
                              <div className='p-1'>
                                {item.assets_type === 'image' ? (
                                  <DynamicImage
                                    src={item.assets_url}
                                    alt={`Media ${index + 1}`}
                                    width={100}
                                    height={100}
                                    className='aspect-square h-auto w-full cursor-pointer rounded border-2 object-cover'
                                    onClick={() => handleMediaClick(item.assets_url, 'image')}
                                    style={
                                      !isEmpty(content.colorSchemeProductDetail)
                                        ? {
                                            borderColor:
                                              item.assets_url === mainImage
                                                ? content.colorSchemeProductDetail?.textSecondary
                                                : 'transparent',
                                          }
                                        : {}
                                    }
                                  />
                                ) : (
                                  <div
                                    className='relative aspect-square w-full cursor-pointer rounded border-2 bg-white'
                                    onClick={() => handleMediaClick(item.assets_url, 'iframe')}
                                  >
                                    <Image
                                      src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                                      alt={`Thumbnail video ${index + 1}`}
                                      width={320}
                                      height={180}
                                      className='h-full w-full rounded bg-white object-contain'
                                    />
                                    <div className='absolute inset-0 flex items-center justify-center'>
                                      <div className='rounded-full bg-slate-200 p-1'>
                                        <Play className='h-6 w-6 text-slate-100' fill='true' />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </CarouselItem>
                          ))}
                    </CarouselContent>
                    {mediaItems.length > 4 && (
                      <>
                        <CarouselPrevious className='left-1/2 top-0 -translate-x-1/2 rotate-90' />
                        <CarouselNext className='-bottom-4 left-1/2 z-10 -translate-x-1/2 rotate-90' />
                      </>
                    )}
                  </Carousel>
                </div>
              )}
              <div className='w-full space-y-4 md:w-1/2'>
                {isSoldOut ? (
                  <div className='w-full'>
                    <Badge
                      variant='custom'
                      className='theme_badge_inside w-full bg-[#616161] text-xs font-bold text-white'
                    >
                      {themeContent?.badge?.soldOutText || 'Sold Out'}
                    </Badge>
                  </div>
                ) : (
                  BADGES_PRODUCT.length > 0 && (
                    <div
                      className={cn(
                        'grid w-full gap-2',
                        BADGES_PRODUCT.length === 1 ? 'grid-cols-1' : '',
                        BADGES_PRODUCT.length === 2 ? 'grid-cols-2' : '',
                        BADGES_PRODUCT.length === 3 ? 'grid-cols-2 grid-rows-2' : ''
                      )}
                    >
                      {BADGES_PRODUCT.map((badge, index) => (
                        <Badge
                          key={index}
                          variant='custom'
                          className={cn(
                            'theme_badge_inside w-full text-xs font-bold',
                            badge.className,
                            BADGES_PRODUCT.length === 3 && index === 2 ? 'col-span-2' : ''
                          )}
                        >
                          {badge.text}
                        </Badge>
                      ))}
                    </div>
                  )
                )}
                {(childs || []).map((child) => (
                  <WrapperChild
                    child={{
                      ...child,
                      parentData: {
                        ...product,
                        ...content,
                        slug: productSlug,
                        token: customerToken,
                        theme: themeContent,
                      },
                      theme,
                    }}
                    parentType={element.type}
                    key={child.id}
                    preview={preview}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {themeContent?.productCard?.positionButtonAddToCart === 'fixed' && (
        <div className={cn('z-50 bg-white shadow-lg transition-transform duration-300')}>
          <div
            id='fixed-add-to-cart'
            className='fixed bottom-0 left-0 right-0 z-[999] rounded-t-3xl border border-b-0 bg-white px-4 py-4 md:mx-20 lg:mx-20 lg:px-10 xl:mx-20 xl:rounded-t-xl'
          >
            <FixedAddToCart product={product} theme={theme} preview={preview} />
          </div>
        </div>
      )}

      {openPreview && (
        <ModalImagePreview
          images={[
            mainImage || '',
            ...images
              .filter((image) => image.assets_url !== mainImage)
              .map((image) => image.assets_url),
          ]}
          initialIndex={[
            mainImage,
            ...images.filter((image) => image.assets_url !== mainImage),
          ].findIndex((img) => img === mainImage)}
          isOpen={openPreview}
          onClose={() => setOpenPreview(false)}
          content={content as ElementDataType}
        />
      )}
      <style jsx>
        {`
          .product_detail_wrapper {
            padding-top: ${content.topPadding}px;
            padding-bottom: ${content.bottomPadding}px;
          }
          .bg_product_detail {
            background-color: ${content.colorSchemeProductDetail?.background || 'white'};
            color: ${content.colorSchemeProductDetail?.text || '#52535B'} !important;
          }
          @media (min-width: 768px) {
            .top-sticky-image {
              top: ${content.topImage || 0}px;
            }
          }
        `}
      </style>
    </>
  );
}
